import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, fmtAmount as fmt, rateFromPercent, unwrap } from "../api";
import {
  Button,
  DeleteButton,
  Empty,
  ErrorNote,
  Field,
  Loading,
  Panel,
  PercentInput,
  SortTh,
  Table,
  TableSearch,
  Td,
  Th,
  input,
  useTable,
} from "../ui";
import { useDeleteRecord } from "../useDeleteRecord";
import { useResource } from "../useResource";
import { ClientPicker } from "./shared";

/** Negative margins are the ones worth spotting from across the room. */
function toneOf(amount: string) {
  return amount.startsWith("-") ? "text-red-600" : "";
}

export function Projects({
  slug,
  currency,
  role,
  onOpenProject,
  onOpenContract,
}: {
  slug: string;
  currency: string;
  role: string;
  onOpenProject: (id: string) => void;
  onOpenContract?: (id: string) => void;
}) {
  const del = useDeleteRecord(slug);
  const [open, setOpen] = useState(false);
  const [clientId, setClientId] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [retainage, setRetainage] = useState("0");

  const clients = useQuery({
    queryKey: ["clients", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"].clients.$get({ param: { slug } })),
  });

  // Same query the Reports overview runs, cached under the same key — a
  // project with no ledger activity simply has no row here, and reads as
  // zero margin below rather than as an error.
  const overview = useQuery({
    queryKey: ["reports-overview", slug],
    queryFn: async () =>
      unwrap(await api.api.c[":slug"].reports.overview.$get({ param: { slug }, query: {} })),
  });
  const marginByProject = new Map(
    (overview.data?.projects ?? []).map((p) => [p.projectId, p.netMargin]),
  );
  const profitByProject = new Map(
    (overview.data?.profitByProject ?? []).map((p) => [p.projectId, p.profit]),
  );

  const r = useResource({
    key: ["projects", slug],
    list: async () => unwrap(await api.api.c[":slug"].projects.$get({ param: { slug } })),
    create: async (body: {
      clientId: string;
      code: string;
      name: string;
      currency: string;
      originalContractValue: string;
      retainagePct: string;
    }) => unwrap(await api.api.c[":slug"].projects.$post({ param: { slug }, json: body })),
  });

  const projectsTable = useTable(r.query.data?.projects ?? [], {
    filter: (p, q) =>
      `${p.code} ${p.name} ${p.clientName} ${p.status}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      code: (p) => p.code.toLowerCase(),
      name: (p) => p.name.toLowerCase(),
      client: (p) => p.clientName.toLowerCase(),
      status: (p) => p.status,
      contract: (p) => Number(p.originalContractValue),
      netMargin: (p) => Number(marginByProject.get(p.id) ?? "0"),
      profit: (p) => Number(profitByProject.get(p.id) ?? "0"),
    },
  });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await r.create({
        clientId,
        code: code.trim(),
        name: name.trim(),
        currency,
        originalContractValue: value || "0",
        retainagePct: rateFromPercent(retainage),
      });
      setCode("");
      setName("");
      setValue("");
      setOpen(false);
    } catch {
      /* surfaced through r.formError */
    }
  }

  const noClients = (clients.data?.clients.length ?? 0) === 0;

  return (
    <Panel
      title="Projects"
      action={
        <Button
          onClick={() => {
            setOpen(!open);
            r.clearErrors();
          }}
        >
          {open ? "Cancel" : "New project"}
        </Button>
      }
    >
      {open && (
        <form
          onSubmit={submit}
          className="mb-6 rounded-xl border border-neutral-200 bg-white p-5 shadow-sm"
        >
          {r.formError && <ErrorNote>{r.formError}</ErrorNote>}
          <div className="grid gap-3 sm:grid-cols-3">
            <ClientPicker
              slug={slug}
              value={clientId}
              onChange={setClientId}
              required
              error={r.fieldErrors.clientId}
            />
            <Field label="Code" error={r.fieldErrors.code}>
              <input
                className={input}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            </Field>
            <Field label="Name" error={r.fieldErrors.name}>
              <input
                className={input}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Field>
            <Field
              label={`Contract value (${currency})`}
              error={r.fieldErrors.originalContractValue}
            >
              <input
                className={input}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="100000.00"
              />
            </Field>
            <Field
              label="Retainage"
              hint="Held back from each invoice"
              error={r.fieldErrors.retainagePct}
            >
              <PercentInput value={retainage} onChange={setRetainage} />
            </Field>
          </div>
          <div className="mt-3">
            <Button type="submit" disabled={r.saving}>
              {r.saving ? "Saving…" : "Create project"}
            </Button>
          </div>
        </form>
      )}

      {del.error && <ErrorNote>{del.error}</ErrorNote>}

      {r.query.isPending ? (
        <Loading />
      ) : r.query.isError ? (
        <ErrorNote>{(r.query.error as Error).message}</ErrorNote>
      ) : r.query.data.projects.length === 0 ? (
        <Empty title="No projects yet">
          {noClients
            ? "Add a client first, then create a project."
            : "Create one to start tracking costs."}
        </Empty>
      ) : (
        <>
          <TableSearch
            value={projectsTable.query}
            onChange={projectsTable.setQuery}
            placeholder="Filter projects…"
          />
          <Table
            head={
              <>
                <SortTh table={projectsTable} sortKey="code">
                  Code
                </SortTh>
                <SortTh table={projectsTable} sortKey="name">
                  Project
                </SortTh>
                <SortTh table={projectsTable} sortKey="client">
                  Client
                </SortTh>
                <SortTh table={projectsTable} sortKey="status">
                  Status
                </SortTh>
                <SortTh table={projectsTable} sortKey="contract" right>
                  Contract
                </SortTh>
                <SortTh table={projectsTable} sortKey="netMargin" right>
                  Net margin
                </SortTh>
                <SortTh table={projectsTable} sortKey="profit" right>
                  Profit
                </SortTh>
                <Th />
              </>
            }
          >
            {/* The whole row opens the project — its schedule, contractors,
                expenses and commissions are all one page down from here. The
                row takes focus and Enter as well as a click, so the shortcut
                isn't mouse-only; the Open button is the same thing named. */}
            {projectsTable.rows.map((p) => (
              <tr
                key={p.id}
                tabIndex={0}
                onClick={() => onOpenProject(p.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onOpenProject(p.id);
                }}
                className="cursor-pointer border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100 focus:bg-neutral-100 focus:outline-none"
              >
                <Td mono muted>
                  {p.code}
                </Td>
                <Td>
                  {p.name}
                  {p.sourceContractId && (
                    <div className="mt-0.5 text-xs font-normal text-neutral-400">
                      {onOpenContract ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenContract(p.sourceContractId!);
                          }}
                          className="underline underline-offset-2 hover:text-neutral-600"
                        >
                          From contract {p.sourceContractNumber}
                        </button>
                      ) : (
                        <>From contract {p.sourceContractNumber}</>
                      )}
                    </div>
                  )}
                </Td>
                <Td muted>{p.clientName}</Td>
                <Td muted>{p.status.replace(/_/g, " ")}</Td>
                <Td right mono>
                  {fmt(p.originalContractValue, p.currency)}
                </Td>
                <Td right mono>
                  <span className={toneOf(marginByProject.get(p.id) ?? "0.00")}>
                    {fmt(marginByProject.get(p.id) ?? "0.00", p.currency)}
                  </span>
                </Td>
                <Td right mono>
                  <span className={toneOf(profitByProject.get(p.id) ?? "0.00")}>
                    {fmt(profitByProject.get(p.id) ?? "0.00", p.currency)}
                  </span>
                </Td>
                {/* Stops the row's own click handler opening the project
                    behind the confirmation the owner is reading. */}
                <Td right>
                  <div
                    className="flex justify-end gap-1"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <Button variant="ghost" onClick={() => onOpenProject(p.id)}>
                      Open
                    </Button>
                    {role === "owner" && (
                      <DeleteButton
                        busy={del.deleting}
                        onConfirm={() => del.remove("project", p.id)}
                      />
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        </>
      )}
    </Panel>
  );
}
